
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAdminAuth } from '../contexts/AdminAuthContext';

type NavLeaf = {
  type?: 'leaf';
  href: string;
  label: string;
  icon: string;
  description: string;
};

type NavGroup = {
  type: 'group';
  key: string;
  label: string;
  icon: string;
  description: string;
  basePath: string;
  children: NavLeaf[];
};

type NavItem = NavLeaf | NavGroup;

const navItems: NavItem[] = [
  { href: '/admin/dashboard', label: 'Dashboard', icon: 'dashboard', description: 'Métricas agregadas' },
  { href: '/admin/empresas', label: 'Empresas', icon: 'business', description: 'Gestion de comercios' },
  { href: '/admin/solicitudes', label: 'Solicitudes', icon: 'request_quote', description: 'Adelantos pendientes' },
  { href: '/admin/alertas', label: 'Alertas', icon: 'notifications_active', description: 'Que requieren atención' },
  { href: '/admin/abandoned-installments', label: 'Cuotas abandonadas', icon: 'block', description: 'FLUX abandonó el cobro' },
  { href: '/admin/pnol-dispatches', label: 'Desembolsos PNOL', icon: 'account_balance', description: 'Lotes BCI a enviar' },
  { href: '/admin/scrapper-events', label: 'Scrappers', icon: 'sensors', description: 'Monitoreo de webhooks' },
  { href: '/admin/cobros', label: 'Cobros', icon: 'receipt_long', description: 'Payment requests (PAC + Lups)' },
  { href: '/admin/lups-webhooks', label: 'Webhooks Lups', icon: 'webhook', description: 'Eventos recibidos' },
  { href: '/admin/calculadora', label: 'Calculadora', icon: 'calculate', description: 'Simular comisión y TYE' },
  { href: '/admin/otps', label: 'OTPs', icon: 'pin', description: 'Codigos generados (read-only)' },
  {
    type: 'group',
    key: 'pac',
    label: 'PAC',
    icon: 'credit_card',
    description: 'Mandatos y nóminas BCI',
    basePath: '/admin/pac',
    children: [
      { href: '/admin/pac/comprobantes', label: 'Comprobantes', icon: 'fact_check', description: 'Activación de mandato' },
      { href: '/admin/pac/universo', label: 'Universo', icon: 'cloud_upload', description: 'Subir archivos BCI' },
      { href: '/admin/pac/batches', label: 'Batches', icon: 'receipt_long', description: 'Nóminas BCI + rendiciones' },
      { href: '/admin/pac/fallidos', label: 'Fallidos', icon: 'error', description: 'Cobros fallidos' },
    ],
  },
  { href: '/admin/tokens', label: 'Tokens', icon: 'key', description: 'Acceso al panel' },
];

export function AdminSidebar() {
  const pathname = useLocation().pathname;
  const { logout } = useAdminAuth();

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    navItems.forEach((item) => {
      if (item.type === 'group' && pathname.startsWith(item.basePath)) {
        initial[item.key] = true;
      }
    });
    return initial;
  });

  const toggleGroup = (key: string) => {
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <aside className="fixed left-0 top-0 bottom-0 w-64 bg-slate-900 text-white flex flex-col border-r border-slate-800 z-40">
      {/* Logo */}
      <div className="px-6 py-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
            <span className="material-symbols-outlined text-white text-[20px]">shield_person</span>
          </div>
          <div>
            <h1 className="text-base font-bold tracking-tight text-white">FLUX</h1>
            <p className="text-[11px] text-slate-400 font-medium tracking-wide uppercase">Admin Panel</p>
          </div>
        </div>
      </div>

      <div className="mx-4 border-t border-slate-800" />

      {/* Navegacion */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        <p className="px-3 text-[10px] font-semibold text-slate-500 uppercase tracking-widest mb-3">Menu</p>
        {navItems.map((item) => {
          if (item.type === 'group') {
            const groupActive = pathname.startsWith(item.basePath);
            const isOpen = openGroups[item.key] ?? false;
            return (
              <div key={item.key}>
                <button
                  type="button"
                  onClick={() => toggleGroup(item.key)}
                  aria-expanded={isOpen}
                  className={cn(
                    'group w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150',
                    groupActive
                      ? 'bg-blue-600/15 text-blue-400'
                      : 'text-slate-400 hover:bg-slate-800/80 hover:text-slate-200'
                  )}
                >
                  <span
                    className={cn(
                      'material-symbols-outlined text-[20px] transition-colors',
                      groupActive ? 'text-blue-400' : 'text-slate-500 group-hover:text-slate-300'
                    )}
                  >
                    {item.icon}
                  </span>
                  <div className="flex flex-col items-start text-left">
                    <span>{item.label}</span>
                    <span className={cn(
                      'text-[10px] font-normal leading-tight',
                      groupActive ? 'text-blue-400/60' : 'text-slate-600'
                    )}>
                      {item.description}
                    </span>
                  </div>
                  <span
                    className={cn(
                      'material-symbols-outlined ml-auto text-[18px] transition-transform duration-200',
                      isOpen ? 'rotate-180' : 'rotate-0',
                      groupActive ? 'text-blue-400' : 'text-slate-500 group-hover:text-slate-300'
                    )}
                  >
                    expand_more
                  </span>
                </button>

                {isOpen && (
                  <div className="mt-1 ml-4 pl-3 border-l border-slate-800 space-y-1">
                    {item.children.map((child) => {
                      const isActive = pathname.startsWith(child.href);
                      return (
                        <Link
                          key={child.href}
                          to={child.href}
                          className={cn(
                            'group flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150',
                            isActive
                              ? 'bg-blue-600/15 text-blue-400'
                              : 'text-slate-400 hover:bg-slate-800/80 hover:text-slate-200'
                          )}
                        >
                          <span
                            className={cn(
                              'material-symbols-outlined text-[18px] transition-colors',
                              isActive ? 'text-blue-400' : 'text-slate-500 group-hover:text-slate-300'
                            )}
                          >
                            {child.icon}
                          </span>
                          <div className="flex flex-col">
                            <span>{child.label}</span>
                            <span className={cn(
                              'text-[10px] font-normal leading-tight',
                              isActive ? 'text-blue-400/60' : 'text-slate-600'
                            )}>
                              {child.description}
                            </span>
                          </div>
                          {isActive && (
                            <div className="ml-auto w-1.5 h-1.5 rounded-full bg-blue-400" />
                          )}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }

          const isActive = pathname.startsWith(item.href);
          return (
            <Link
              key={item.href}
              to={item.href}
              className={cn(
                'group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150',
                isActive
                  ? 'bg-blue-600/15 text-blue-400'
                  : 'text-slate-400 hover:bg-slate-800/80 hover:text-slate-200'
              )}
            >
              <span
                className={cn(
                  'material-symbols-outlined text-[20px] transition-colors',
                  isActive ? 'text-blue-400' : 'text-slate-500 group-hover:text-slate-300'
                )}
              >
                {item.icon}
              </span>
              <div className="flex flex-col">
                <span>{item.label}</span>
                <span className={cn(
                  'text-[10px] font-normal leading-tight',
                  isActive ? 'text-blue-400/60' : 'text-slate-600'
                )}>
                  {item.description}
                </span>
              </div>
              {isActive && (
                <div className="ml-auto w-1.5 h-1.5 rounded-full bg-blue-400" />
              )}
            </Link>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="px-3 py-4 border-t border-slate-800">
        <button
          onClick={logout}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-500 hover:bg-slate-800/80 hover:text-red-400 transition-all duration-150 w-full group"
        >
          <span className="material-symbols-outlined text-[20px] text-slate-600 group-hover:text-red-400 transition-colors">logout</span>
          Cerrar sesion
        </button>
      </div>
    </aside>
  );
}
